import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { Announcement } from '@internal/backstage-plugin-announcement-common';
import { announcementApiRef } from '../api/AnnouncementApi';

/**
 * Manages announcement data and dismiss state for the AnnouncementBanner.
 *
 * Two-phase progressive fetch strategy (skipped when `initialAnnouncements` is
 * provided):
 *   Phase 1 – fetchPublicAnnouncements() uses globalThis.fetch (no identity
 *             middleware) so it resolves immediately, even before sign-in.
 *   Phase 2 – isAuthenticated() blocks on AppIdentityProxy.waitForTarget until
 *             the user signs in. Once resolved, upgrade to active announcements.
 *
 * Both fetch paths return [] on any error so the banner never breaks the app.
 */
export const useAnnouncementBanner = (
  initialAnnouncements?: Announcement[],
): {
  visibleAnnouncements: Announcement[];
  handleClose: (id: string) => void;
  isAuthenticated: boolean;
} => {
  const api = useApi(announcementApiRef);

  const [announcements, setAnnouncements] = useState<Announcement[]>(
    initialAnnouncements ?? [],
  );
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    if (initialAnnouncements) return undefined;

    let cancelled = false;

    const load = async () => {
      // Phase 1: show public announcements straight away (works pre-auth)
      const publicAnnouncements = await api.fetchPublicAnnouncements();
      if (!cancelled) setAnnouncements(publicAnnouncements);

      // Phase 2: after sign-in resolves, upgrade to the full active list
      try {
        const authenticated = await api.isAuthenticated();
        if (authenticated && !cancelled) {
          setIsAuthenticated(true);
          const activeAnnouncements = await api.fetchActiveAnnouncements();
          if (!cancelled) setAnnouncements(activeAnnouncements);
        }
      } catch {
        // Not authenticated or error — keep showing public announcements
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [api, initialAnnouncements]);

  const visibleAnnouncements = announcements.filter(
    a => !dismissedIds.has(a.id),
  );

  const handleClose = (id: string) => {
    setDismissedIds(prev => new Set(prev).add(id));
  };

  return { visibleAnnouncements, handleClose, isAuthenticated };
};
